import { useEffect } from 'react';
import { positions, Provider as AlertProvider, transitions } from 'react-alert';
import AlertTemplate from 'react-alert-template-oldschool-dark';
import { Provider, useSelector } from 'react-redux';
import { createTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { withProfiler } from '@sentry/react';
import { PersistGate } from 'redux-persist/integration/react';

import GlobalHooksComponent from '@lootboy/lib/components/GlobalHooksComponent';
import { loggedSelector } from '@lootboy/lib/selectors';

import ActivityIndicator from '~/components/ActivityIndicator';
import CookieConsentsProvider from '~/components/CookieConsentsProvider';

import { clearTags, sendTags } from '~/middlewares/onesignal';
import { miscSelector, userSelector } from '~/redux/selectors';
import configureStore from '~/redux/store';
import Router from '~/routes';

const { store, onBeforeLift, persistor } = configureStore();

const alertOptions = {
  offset: '14px',
  position: positions.TOP_RIGHT,
  timeout: 5000,
  transition: transitions.SCALE,
  containerStyle: {
    zIndex: 99999999,
  },
};

const theme = createTheme({
  overrides: {
    MuiIconButton: {
      root: {
        color: 'white',
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: '26px',
      },
    },
  },
});

// todo find place for it
const OneSignalObserver = () => {
  const { _id: userId } = useSelector(userSelector);
  const isLoggedIn = useSelector(loggedSelector);
  const { notificationsEnabled } = useSelector(
    ({ dailyBoostInterval }) => dailyBoostInterval,
  );
  const { jobsNotificationsEnabled } = useSelector(miscSelector);

  const { lastSuccessfulFetch, progress = {} } = useSelector(
    ({ intervalProgress }) => intervalProgress,
  );
  const { lastClaimAt, currentProgress } = progress;

  useEffect(() => {
    if (userId) {
      sendTags({ userId });
    } else {
      clearTags();
    }
  }, [userId]);

  useEffect(() => {
    if (isLoggedIn) {
      let allowDailyBoostNotifications = '';
      let allowJobsNotifications = '';

      if (jobsNotificationsEnabled) {
        allowJobsNotifications = 'true';
      }

      if (notificationsEnabled) {
        allowDailyBoostNotifications = 'true';
      }

      if (notificationsEnabled || jobsNotificationsEnabled) {
        window.OneSignal.push(() => {
          // eslint-disable-next-line max-nested-callbacks
          window.OneSignal.isPushNotificationsEnabled().then((isEnabled) => {
            if (!isEnabled) {
              window.OneSignal.showSlidedownPrompt();
            }
          });
        });
      }

      sendTags({
        allowDailyBoostNotifications,
        allowJobsNotifications,
      });
    }
  }, [isLoggedIn, notificationsEnabled, jobsNotificationsEnabled]);

  useEffect(() => {
    if (isLoggedIn && lastSuccessfulFetch) {
      const dailyBoostLastClaim = lastClaimAt
        ? Math.floor(new Date(lastClaimAt).getTime() / 1000)
        : '';
      sendTags({
        dailyBoostLastClaim,
        dailyBoostIntervalStep: currentProgress,
      });
    }
  }, [currentProgress, isLoggedIn, lastClaimAt, lastSuccessfulFetch]);

  return null;
};

const App = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <PersistGate
        loading={<ActivityIndicator />}
        onBeforeLift={onBeforeLift}
        persistor={persistor}
      >
        <OneSignalObserver />
        <AlertProvider
          template={AlertTemplate}
          {...alertOptions}
        >
          <CookieConsentsProvider>
            <GlobalHooksComponent />
            <Router />
          </CookieConsentsProvider>
        </AlertProvider>
      </PersistGate>
    </ThemeProvider>
  </Provider>
);

export default withProfiler(App);
